import { v4 as uuidv4 } from "uuid";
import type { StoreApi } from "zustand";
import type { Action, MediaItem } from "@openreel/core";
import type { ProjectState } from "../project-store";
import { getMediaBridge, initializeMediaBridge } from "../../bridges/media-bridge";
import { saveMediaBlob } from "../../services/media-storage";

type Get = StoreApi<ProjectState>["getState"];
type Set = StoreApi<ProjectState>["setState"];

/** What a background pass should rebuild for one media item. */
export interface DerivedMediaRequest {
  id: string;
  type: MediaItem["type"];
  /** The decodable source. For a transcoded import this is the transcoded file. */
  blob: Blob;
  thumbnails: boolean;
  waveform: boolean;
}

/**
 * One at a time. Every decode below is main-thread work, so a ten file drop
 * firing ten of them at once would recreate exactly the stall this moves out of
 * the import path.
 */
let hydrationQueue: Promise<void> = Promise.resolve();

/**
 * Rebuild the derived visuals for a media item after it is already in the
 * library: filmstrip thumbnails and the waveform.
 *
 * Neither is needed to place a clip (the executor only wants a duration) and
 * neither is persisted any more, so they are generated once the item is on
 * screen rather than before it appears. This is the same shape as the old
 * best-effort thumbnail retry, promoted from a fallback to the only path.
 */
export function hydrateDerivedMedia(
  set: Set,
  get: Get,
  request: DerivedMediaRequest,
): Promise<void> {
  const run = hydrationQueue.then(() =>
    runDerivedMediaHydration(set, get, request),
  );
  // A failure must not break the chain for everything queued behind it.
  hydrationQueue = run.catch(() => undefined);
  return hydrationQueue;
}

/**
 * Re-encode an object-URL poster as a data: URL so it can actually be stored.
 *
 * mediabunny hands back object URLs for video, and an object URL is dead after
 * a reload, so every consumer downstream drops it: the patch below skips the
 * save, toPersistableProject strips it, and the autosave header records no
 * thumbnail. The visible result was a Recent Projects wall of identical film
 * glyphs, because a poster only ever survived for images.
 *
 * Only the poster is converted, never the filmstrip. Ten inline JPEGs per item
 * would bloat every project write, and recovery discards filmstrips anyway.
 */
async function posterAsDataUrl(url: string): Promise<string | null> {
  if (url.startsWith("data:")) return url;
  try {
    const blob = await (await fetch(url)).blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () =>
        resolve(typeof reader.result === "string" ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    // A revoked or cross-origin URL is not worth failing the import over.
    return null;
  }
}

async function runDerivedMediaHydration(
  set: Set,
  get: Get,
  request: DerivedMediaRequest,
): Promise<void> {
  const mediaBridge = getMediaBridge();
  if (!mediaBridge.isInitialized()) {
    try {
      await initializeMediaBridge();
    } catch {
      return;
    }
  }

  if (request.thumbnails && isStillInLibrary(get, request.id)) {
    try {
      const thumbs = await mediaBridge.generateThumbnailsForMedia(
        request.blob,
        request.type,
      );
      if (thumbs.length > 0) {
        // Falls back to the raw url so the session still shows a poster even
        // when re-encoding fails; it just will not outlive the reload.
        const poster =
          (await posterAsDataUrl(thumbs[0].dataUrl)) ?? thumbs[0].dataUrl;
        const applied = patchMediaItem(
          set,
          get,
          request.id,
          (item) => {
            // The strip being replaced is dead the moment the new one lands.
            // This matters more than it used to: hydration runs on every
            // project open for every visual item, not once at import, so
            // without this a reopened thirty clip project pins another three
            // hundred thumbnail blobs each time.
            revokeObjectUrls([
              item.thumbnailUrl,
              ...(item.filmstripThumbnails ?? []).map((thumb) => thumb.url),
            ]);
            return {
              ...item,
              thumbnailUrl: poster,
              filmstripThumbnails: thumbs.map((thumb) => ({
                timestamp: thumb.timestamp,
                url: thumb.dataUrl,
              })),
            };
          },
          // Only a data: URL is worth a save. toPersistableProject drops an
          // object-URL thumbnail on the very next write, because a blob: URL
          // is dead after a reload, so touching modifiedAt for one buys a full
          // whole-project save that stores nothing. mediabunny's
          // generateThumbnails hands back object URLs for video despite the
          // field being called dataUrl; the image branch and the video-element
          // fallback are the ones that really do produce data: URLs.
          { touch: poster.startsWith("data:") },
        );
        // Nobody is holding these if the patch was dropped.
        if (!applied) {
          revokeObjectUrls(thumbs.map((thumb) => thumb.dataUrl));
        }
      }
    } catch {
      // Best effort: the item keeps its type icon.
    }
  }

  if (request.waveform && isStillInLibrary(get, request.id)) {
    try {
      const waveform = await mediaBridge.generateWaveform(
        request.blob,
        request.id,
      );
      if (waveform) {
        patchMediaItem(
          set,
          get,
          request.id,
          (item) => ({ ...item, waveformData: waveform.peaks }),
          // Waveforms are stripped before writing, so bumping modifiedAt here
          // would only schedule a save with nothing new in it.
          { touch: false },
        );
      }
    } catch {
      // Best effort: waveform consumers already fall back to placeholder bars.
    }
  }
}

function isStillInLibrary(get: Get, mediaId: string): boolean {
  return get().project.mediaLibrary.items.some((item) => item.id === mediaId);
}

/**
 * Release object URLs we are about to stop referencing. Anything that is not a
 * blob: URL is left alone: a data: URL owns no handle, and revoking a string
 * that is not an object URL is a silent no-op that only hides mistakes.
 */
function revokeObjectUrls(urls: (string | null | undefined)[]): void {
  for (const url of urls) {
    if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
  }
}

/**
 * Fold a background result into the library by id, against whatever the store
 * holds now. A decode outlives the edit that started it, so the item may have
 * been deleted or replaced meanwhile; then the result is simply dropped.
 *
 * Returns whether the update was applied, so a caller holding resources that
 * only the store would own (object URLs) can release them when it was not.
 */
function patchMediaItem(
  set: Set,
  get: Get,
  mediaId: string,
  update: (item: MediaItem) => MediaItem,
  options: { touch: boolean },
): boolean {
  const current = get().project;
  const index = current.mediaLibrary.items.findIndex(
    (item) => item.id === mediaId,
  );
  if (index === -1) return false;

  const items = [...current.mediaLibrary.items];
  items[index] = update(items[index]);

  set({
    project: {
      ...current,
      mediaLibrary: { ...current.mediaLibrary, items },
      ...(options.touch ? { modifiedAt: Date.now() } : {}),
    },
  });
  return true;
}

export type MediaSlice = Pick<
  ProjectState,
  | "importMedia"
  | "deleteMedia"
  | "replaceMediaAsset"
  | "renameMedia"
  | "getMediaItem"
>;

export function createMediaSlice(set: Set, get: Get): MediaSlice {
  return {
    importMedia: async (file: File) => {
      const { project } = get();

      try {
        const mediaBridge = getMediaBridge();
        if (!mediaBridge.isInitialized()) {
          await initializeMediaBridge();
        }

        // Metadata only. Thumbnails are ten seeks and a waveform is a full
        // sample-by-sample decode of the file, and both used to run before the
        // item existed, which is why dropping a long audio file read as a
        // hang. They stream in from hydrateDerivedMedia below. This is the
        // path large files have always taken, so it is well travelled.
        const importResult = await mediaBridge.importFile(file, false, true);

        if (!importResult.success || !importResult.media) {
          return {
            success: false,
            error: {
              code: "DECODE_ERROR" as const,
              message: importResult.error || "Failed to import media",
            },
          };
        }

        const processedMedia = importResult.media;

        let mediaType: "video" | "audio" | "image";
        if (file.type.startsWith("image/")) {
          mediaType = "image";
        } else if (processedMedia.metadata.hasVideo) {
          mediaType = "video";
        } else if (processedMedia.metadata.hasAudio) {
          mediaType = "audio";
        } else {
          mediaType = "image";
        }

        const newMediaItem: MediaItem = {
          id: uuidv4(),
          name: file.name,
          type: mediaType,
          fileHandle: null,
          blob: file,
          metadata: {
            duration: processedMedia.metadata.duration || 0,
            width: processedMedia.metadata.width || 0,
            height: processedMedia.metadata.height || 0,
            frameRate: processedMedia.metadata.frameRate || 0,
            codec: processedMedia.metadata.codec || "",
            sampleRate: processedMedia.metadata.sampleRate || 0,
            channels: processedMedia.metadata.channels || 0,
            fileSize: file.size,
          },
          thumbnailUrl: null,
          waveformData: null,
          sourceFile: {
            name: file.name,
            size: file.size,
            lastModified: file.lastModified,
          },
        };

        const updatedProject = {
          ...project,
          mediaLibrary: {
            ...project.mediaLibrary,
            items: [...project.mediaLibrary.items, newMediaItem],
          },
          modifiedAt: Date.now(),
        };

        set({ project: updatedProject });

        try {
          await saveMediaBlob(
            updatedProject.id,
            newMediaItem.id,
            file,
            newMediaItem.metadata,
          );
        } catch (err) {
          console.error("[ProjectStore] Failed to persist media blob:", err);
        }

        // Deliberately not awaited: the clip is usable now, the pictures and
        // the waveform arrive when they arrive. processedMedia.blob is the
        // decodable source, which differs from `file` when the import had to
        // transcode through the FFmpeg fallback.
        void hydrateDerivedMedia(set, get, {
          id: newMediaItem.id,
          type: mediaType,
          blob: processedMedia.blob ?? file,
          thumbnails: mediaType !== "audio",
          waveform: Boolean(processedMedia.metadata.hasAudio),
        });

        return { success: true, actionId: newMediaItem.id };
      } catch (error) {
        return {
          success: false,
          error: {
            code: "DECODE_ERROR" as const,
            message:
              error instanceof Error ? error.message : "Unknown import error",
          },
        };
      }
    },

    deleteMedia: async (mediaId: string) => {
      const { project, actionExecutor } = get();
      const action: Action = {
        type: "media/delete",
        id: uuidv4(),
        timestamp: Date.now(),
        params: { mediaId },
      };
      const result = await actionExecutor.execute(action, project);
      if (result.success) {
        set({ project: { ...project } });
        // The blob deliberately stays. media/delete is undoable, and undo
        // restores the library entry from the action history, but nothing can
        // restore the file itself: deleting it here meant undo handed back a
        // clip whose source was gone, which then survived into the autosave.
        // Orphaned blobs are reclaimed when the project itself is discarded,
        // and a stale blob costs disk while a missing one costs the edit.
      }
      return result;
    },

    replaceMediaAsset: async (
      mediaId: string,
      file: File,
      sourceFolder?: string,
    ) => {
      const { project } = get();

      try {
        const mediaBridge = getMediaBridge();
        if (!mediaBridge.isInitialized()) {
          await initializeMediaBridge();
        }

        // Metadata only, same reasoning as importMedia: the swap should land
        // immediately and the pictures follow.
        const importResult = await mediaBridge.importFile(file, false, true);

        if (!importResult.success || !importResult.media) {
          return {
            success: false,
            error: {
              code: "DECODE_ERROR" as const,
              message: importResult.error || "Failed to import media",
            },
          };
        }

        const processedMedia = importResult.media;

        const mediaType = processedMedia.metadata.hasVideo
          ? "video"
          : processedMedia.metadata.hasAudio
            ? "audio"
            : "image";

        const updatedItem: MediaItem = {
          id: mediaId,
          name: file.name,
          type: mediaType,
          fileHandle: null,
          blob: file,
          metadata: {
            duration: processedMedia.metadata.duration || 0,
            width: processedMedia.metadata.width || 0,
            height: processedMedia.metadata.height || 0,
            frameRate: processedMedia.metadata.frameRate || 0,
            codec: processedMedia.metadata.codec || "",
            sampleRate: processedMedia.metadata.sampleRate || 0,
            channels: processedMedia.metadata.channels || 0,
            fileSize: file.size,
          },
          thumbnailUrl: null,
          waveformData: null,
          isPlaceholder: false,
          sourceFile: {
            name: file.name,
            size: file.size,
            lastModified: file.lastModified,
            folder: sourceFolder,
          },
        };

        const updatedItems = project.mediaLibrary.items.map((item) => {
          if (item.id !== mediaId) return item;
          // The outgoing file's pictures go with it. Nothing can undo a
          // replace, so nobody comes back for these.
          revokeObjectUrls([
            item.thumbnailUrl,
            ...(item.filmstripThumbnails ?? []).map((thumb) => thumb.url),
          ]);
          return updatedItem;
        });

        set({
          project: {
            ...project,
            mediaLibrary: { items: updatedItems },
            modifiedAt: Date.now(),
          },
        });

        try {
          // The replacement has to reach the media store too. Without this the
          // item kept pointing at the file it replaced: recovery reattached the
          // OLD blob to an item carrying the NEW name, duration and dimensions.
          await saveMediaBlob(
            project.id,
            mediaId,
            file,
            updatedItem.metadata,
          );
        } catch (err) {
          console.error(
            "[ProjectStore] Failed to persist replacement media blob:",
            err,
          );
        }

        void hydrateDerivedMedia(set, get, {
          id: mediaId,
          type: mediaType,
          blob: processedMedia.blob ?? file,
          thumbnails: mediaType !== "audio",
          waveform: Boolean(processedMedia.metadata.hasAudio),
        });

        return { success: true, actionId: uuidv4() };
      } catch (error) {
        return {
          success: false,
          error: {
            code: "DECODE_ERROR" as const,
            message:
              error instanceof Error ? error.message : "Unknown import error",
          },
        };
      }
    },

    renameMedia: async (mediaId: string, name: string) => {
      const { project, actionExecutor } = get();
      const action: Action = {
        type: "media/rename",
        id: uuidv4(),
        timestamp: Date.now(),
        params: { mediaId, name },
      };
      const result = await actionExecutor.execute(action, project);
      if (result.success) {
        set({ project: { ...project } });
      }
      return result;
    },

    getMediaItem: (mediaId: string) =>
      get().project.mediaLibrary.items.find((item) => item.id === mediaId),
  };
}
